import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type Clip = { question_id: string; question_text: string | null; url: string };
type SeamResponse = { enabled: boolean; clips: Clip[] };

type State =
  | { status: "loading" }
  | { status: "ready"; enabled: boolean; clips: Clip[] }
  | { status: "error" };

interface OriginalRecordingsProps {
  slug: string;
  name: string;
}

// Public per-question raw-recording player. This component is the SOLE arbiter of
// its own visibility — TributePage renders it unconditionally and stays ignorant.
// It renders nothing unless the tribute-raw-audio seam returns enabled with clips,
// so it is invisible whenever the release flag is off (the common case today).
const OriginalRecordings = ({ slug, name }: OriginalRecordingsProps) => {
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    supabase.functions
      .invoke<SeamResponse>("tribute-raw-audio", { body: { slug } })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setState({ status: "error" });
          return;
        }
        setState({
          status: "ready",
          enabled: !!data.enabled,
          clips: data.clips ?? [],
        });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Render nothing in every case except a successful, enabled response with clips.
  // No skeleton on loading: a placeholder for a section that usually will not
  // appear is worse UX than its silent absence. No retry on audio error (v1).
  if (state.status !== "ready") return null; // loading or error -> absent
  if (!state.enabled) return null;
  if (state.clips.length === 0) return null;

  return (
    <section className="mb-12">
      <h2 className="font-display text-2xl font-semibold text-foreground text-center mb-1">
        In Their Own Voice
      </h2>
      <p className="text-center text-muted-foreground text-sm mb-6">
        {`${name}'s original recordings, unedited.`}
      </p>
      <div className="space-y-6">
        {state.clips.map((clip) => (
          <div
            key={clip.question_id}
            className="rounded-xl border border-border/30 bg-card p-4"
          >
            {clip.question_text && (
              <p className="text-foreground/90 text-sm leading-relaxed mb-3">
                {clip.question_text}
              </p>
            )}
            <audio controls preload="none" src={clip.url} className="w-full">
              Your browser does not support audio playback.
            </audio>
          </div>
        ))}
      </div>
    </section>
  );
};

export default OriginalRecordings;
